import { DirectionProvider } from '@radix-ui/react-direction';
import {
  displayServerMask,
  formatToman,
  formatTomanCompact,
  LTR_ISOLATE_PROPS,
  type MaskedKind,
  type Rials,
} from '@saay/formatting';
import { setNonce } from 'get-nonce';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode, useEffect, useState } from 'react';

import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';
import { TooltipProvider } from './overlay-kit.js';
import { SnackbarProvider } from './snackbar.js';
import { ToastProvider } from './toast.js';

/**
 * Root provider: RTL direction for every Radix primitive and the toast region. Apps render it once,
 * inside `<html lang="fa" dir="rtl">`.
 */
export function UiProvider({ children }: { readonly children: ReactNode }) {
  return (
    <DirectionProvider dir="rtl">
      <TooltipProvider>
        <ToastProvider>
          <SnackbarProvider>{children}</SnackbarProvider>
        </ToastProvider>
      </TooltipProvider>
    </DirectionProvider>
  );
}

/**
 * Passes the page's CSP nonce (from `<meta property="csp-nonce">`, written by Vite `html.cspNonce`)
 * to the style tags Radix injects at runtime, so `style-src` needs no `'unsafe-inline'`.
 */
export function configureCspNonce(documentRef: Document = document): void {
  const meta = documentRef.querySelector<HTMLMetaElement>('meta[property="csp-nonce"]');
  const nonce = meta?.nonce || meta?.getAttribute('nonce') || '';
  if (nonce !== '') {
    setNonce(nonce);
  }
}

export type Md3ButtonAppearance = 'filled' | 'tonal' | 'outlined' | 'text' | 'elevated';

export type ButtonVariant = Md3ButtonAppearance | 'primary' | 'secondary' | 'ghost' | 'danger';

export type IconButtonVariant = 'standard' | 'filled' | 'tonal' | 'outlined';

function resolveButtonAppearance(variant: ButtonVariant): {
  readonly appearance: Md3ButtonAppearance;
  readonly destructive: boolean;
  readonly legacyVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
} {
  switch (variant) {
    case 'primary':
      return { appearance: 'filled', destructive: false, legacyVariant: 'primary' };
    case 'secondary':
      return { appearance: 'outlined', destructive: false, legacyVariant: 'secondary' };
    case 'ghost':
      return { appearance: 'text', destructive: false, legacyVariant: 'ghost' };
    case 'danger':
      return { appearance: 'filled', destructive: true, legacyVariant: 'danger' as const };
    default:
      return { appearance: variant, destructive: false };
  }
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Blocked actions stay focusable with `aria-disabled` and an explanatory `title`. */
  readonly blocked?: boolean;
  readonly blockedReason?: string;
  /** Stretches the button to its container (prototype `.full-width`). */
  readonly fullWidth?: boolean;
  /** A leading prototype icon. */
  readonly icon?: IconName;
  /** Shows the pending state: disabled, `aria-busy`, and the label kept for screen readers. */
  readonly loading?: boolean;
  readonly size?: 'md' | 'sm';
  readonly trailingIcon?: IconName;
  readonly variant?: ButtonVariant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    blocked = false,
    blockedReason,
    children,
    className,
    disabled,
    fullWidth = false,
    icon,
    loading = false,
    onClick,
    size = 'md',
    trailingIcon,
    type = 'button',
    variant = 'primary',
    ...rest
  },
  ref,
) {
  const resolved = resolveButtonAppearance(variant);
  const isDisabled = disabled === true || loading;
  const isBlocked = blocked && !isDisabled;

  return (
    <button
      {...rest}
      ref={ref}
      aria-busy={loading || undefined}
      aria-disabled={isBlocked || undefined}
      className={['saay-button', 'saay-state', className].filter(Boolean).join(' ')}
      data-appearance={resolved.appearance}
      data-destructive={resolved.destructive || undefined}
      data-full={fullWidth || undefined}
      data-size={size}
      {...(resolved.legacyVariant === undefined ? {} : { 'data-variant': resolved.legacyVariant })}
      disabled={isDisabled}
      onClick={(event) => {
        if (isBlocked) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      title={isBlocked ? blockedReason : rest.title}
      type={type}
    >
      {loading ? <span aria-hidden="true" className="saay-button__spinner" /> : null}
      {icon === undefined || loading ? null : <Icon name={icon} />}
      <span className="saay-button__label">{children}</span>
      {trailingIcon === undefined || loading ? null : <Icon name={trailingIcon} />}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly blocked?: boolean;
  readonly blockedReason?: string;
  readonly icon: IconName;
  /** The accessible name (the button shows only the icon). */
  readonly label: string;
  readonly loading?: boolean;
  readonly variant?: IconButtonVariant;
}

/** A round icon-only button (prototype `.icon-button`), named by `label`. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    blocked = false,
    blockedReason,
    className,
    icon,
    label,
    loading = false,
    onClick,
    type = 'button',
    variant = 'standard',
    ...rest
  },
  ref,
) {
  const isDisabled = rest.disabled === true || loading;
  const isBlocked = blocked && !isDisabled;

  return (
    <button
      {...rest}
      aria-busy={loading || undefined}
      aria-disabled={isBlocked || undefined}
      aria-label={label}
      className={['saay-icon-button', 'saay-state', className].filter(Boolean).join(' ')}
      data-variant={variant}
      disabled={isDisabled}
      onClick={(event) => {
        if (isBlocked) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      ref={ref}
      title={isBlocked ? blockedReason : label}
      type={type}
    >
      {loading ? (
        <span aria-hidden="true" className="saay-button__spinner" />
      ) : (
        <Icon name={icon} />
      )}
    </button>
  );
});

/** A left-to-right identifier inside RTL text: references, mobile, Sheba, request IDs (FTD §3.3). */
export function Ltr({
  children,
  mask = false,
}: {
  readonly children: ReactNode;
  /** A masked mobile, national code, or card in Persian digits: the larger mask bullet. */
  readonly mask?: boolean;
}) {
  return (
    <bdi {...LTR_ISOLATE_PROPS} className="saay-ltr" data-mask={mask || undefined}>
      {children}
    </bdi>
  );
}

/**
 * A server-masked identifier (`*Masked` fields: mobile, national code, Sheba, masked names): shown
 * with `•` instead of the API's `*`, left to right inside RTL text (FTD §3.3). Use it for every masked
 * value so the rule lives in one place (`displayServerMask` in `@saay/formatting`).
 */
export function Masked({
  kind = 'text',
  value,
}: {
  readonly kind?: MaskedKind;
  readonly value: string;
}) {
  // Persian-digit masks take the larger bullet (styles: `.saay-ltr[data-mask]`).
  const persian = kind === 'mobile' || kind === 'nationalCode' || kind === 'card';
  return <Ltr mask={persian}>{displayServerMask(value, kind)}</Ltr>;
}

/**
 * A money amount from integer rials, shown in toman with tabular digits. `compact` truncates and is
 * for dashboards and summary cards only, never for payable amounts (FTD §3.1).
 */
export function Money({
  compact = false,
  rials,
}: {
  readonly compact?: boolean;
  readonly rials: Rials;
}) {
  return (
    <span className="saay-amount">{compact ? formatTomanCompact(rials) : formatToman(rials)}</span>
  );
}

/** Loading placeholder matching the layout (FTD §11: no spinner-only pages). */
export function Skeleton({
  label = 'در حال بارگذاری…',
  lines = 3,
}: {
  readonly label?: string;
  readonly lines?: number;
}) {
  return (
    <div aria-busy="true" aria-live="polite" className="saay-skeleton" role="status">
      <span className="saay-visually-hidden">{label}</span>
      {Array.from({ length: lines }, (_, index) => (
        <span
          aria-hidden="true"
          className="saay-skeleton__line"
          key={index}
          style={{ inlineSize: `${index === lines - 1 ? 60 : 100}%` }}
        />
      ))}
    </div>
  );
}

/** `navigator.onLine` as React state. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/** FTD §11 offline banner; financial mutations must be disabled while it shows. */
export function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) {
    return null;
  }
  return (
    <div className="saay-banner" data-tone="warning" role="status">
      <Icon name="alert" />
      <span>
        اینترنت وصل نیست. تا وصل شدن دوباره، اطلاعات مالی به‌روز نمی‌شود و کارهای مالی غیرفعال است.
      </span>
    </div>
  );
}

export function Banner({
  children,
  icon,
  tone,
}: {
  readonly children: ReactNode;
  /** Overrides the tone icon (for example the prototype shield on security notes). */
  readonly icon?: IconName;
  readonly tone: 'info' | 'success' | 'warning' | 'danger';
}) {
  return (
    <div className="saay-banner" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon ?? BANNER_ICON[tone]} />
      <div>{children}</div>
    </div>
  );
}

const BANNER_ICON = {
  danger: 'alert',
  info: 'info',
  success: 'check',
  warning: 'alert',
} as const satisfies Record<string, IconName>;
